import { bindActionCreators } from 'redux';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';

/**
 * useActions - Takes a single, array, or object of redux actions and converts it to usable functions wrapped in dispatch
 * @see https://react-redux.js.org/next/api/hooks#recipe-useactions
 *
 * @param {func||array||object}  actions - single redux action creator (function), array or object of action creators
 * @param {array}                deps - array of dependencies. If any of the actions are built with values that may
 *                               change during the lifetime of the component, these need to be passed to deps
 *
 * @example
 * // simple use case:
 * const handler = useActions(reduxActionCreator);
 * // with deps:
 * const handler = useActions(() => reduxActionCreator(dynamicVariable), [dynamicVariable]);
 * // multiple actions:
 * const [handler1, handler2] = useActions([reduxActionCreator1, reduxActionCreator2])
 * // actions object:
 * import * as reservationActions from 'actions/reservation';
 * const { selectCarClass, getCarClassDetails } = useActions(reservationActions);
 *
 * @return {func||array||object}  returns single or array or object of functions
 */
export default function useActions(actions, deps) {
  const dispatch = useDispatch();
  return useMemo(
    () => {
      if (Array.isArray(actions)) {
        return actions.map(a => bindActionCreators(a, dispatch));
      }
      return bindActionCreators(actions, dispatch);
    },
    deps ? [dispatch, ...deps] : [dispatch]
  );
}
